import { z } from 'zod';
/*
  coercedNumber returns a Zod schema that coerces string inputs to numbers.
  This is useful for query parameters which are always received as strings.

  Usage:
    coercedNumber().int().min(1).max(100).default(20)
    coercedNumber().positive().default(1)
*/
export const coercedNumber = () => z.coerce.number();
/*
  coercedBoolean returns a Zod schema that accepts a real boolean OR the
  literal string "true"/"false" (case-insensitive) and produces a boolean.

  Required because `z.coerce.boolean()` treats any non-empty string as `true`,
  so the literal string "false" would incorrectly become `true` when sent via a
  query parameter. Use this for query-string boolean fields.

  Strict by design: only "true"/"false" are accepted as strings. Values like
  "1", "0", "yes", "no", "on", "off" are rejected so broken clients fail loudly
  rather than silently producing a coerced boolean.

  Usage:
    includeMuted: coercedBoolean().optional().default(false)
*/
export const coercedBoolean = () => z.union([z.boolean(), z.stringbool({ truthy: ['true'], falsy: ['false'] })]);
/**
 * Standard response when a feature-flagged integration is disabled.
 * Shared across all integration routes (GitHub, Bitbucket, etc.).
 */
export const featureDisabledResponseSchema = z.object({
    enabled: z.literal(false),
});
// ============================================================================
// Generic List Query Schemas
// ============================================================================
// These schemas provide a consistent structure for all sortable/filterable
// list endpoints. Domain-specific query schemas should extend baseListQuerySchema.
/**
 * Generic sort order used by all list endpoints.
 */
export var SortOrder;
(function (SortOrder) {
    SortOrder["Asc"] = "asc";
    SortOrder["Desc"] = "desc";
})(SortOrder || (SortOrder = {}));
export const sortOrderSchema = z.nativeEnum(SortOrder);
/**
 * Base pagination schema for list endpoints.
 * Uses coerce for query string compatibility.
 */
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    limit: z.coerce.number().int().min(1).max(100).default(50),
});
/**
 * Standard pagination metadata returned by list endpoints.
 */
export const paginationResponseSchema = z.object({
    page: z.number().int().min(1),
    limit: z.number().int().min(1).max(100),
    total: z.number().int().min(0),
    hasMore: z.boolean(),
});
/**
 * Base list query schema with pagination and optional search.
 * Extend this with domain-specific sort and filter fields.
 *
 * @example
 * ```ts
 * // In domain contract (e.g., mcp.ts):
 * export const mcpListQuerySchema = baseListQuerySchema.extend({
 *   sortBy: mcpSortFieldSchema.default('userCount'),
 *   sortOrder: sortOrderSchema.default('desc'),
 *   transport: mcpTransportSchema.optional(),
 * });
 * ```
 */
export const baseListQuerySchema = paginationSchema.extend({
    search: z.string().optional(),
});
//# sourceMappingURL=helpers.js.map